import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'lib-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() error = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() success = '';
  @Input() textarea = false;
  @Input() required: boolean | string = false; // accept string to avoid messup requirement from other component. it should handled soon
  @Input() type: string = 'text';
  @Input() id = '';
  @Input() name = '';
  @Input() autocomplete = '';
  @Input() set value(val: string | Date | Date[] | null) {
    this._value = val === null || val === undefined ? '' : val;
  }
  get value(): string | Date | Date[] | null {
    return this._value;
  }
  @Input() set iconLeft(value: string | TemplateRef<any> | undefined) {
    this._iconLeft = value;
    this.sanitizedIconLeft =
      typeof value === 'string' && value
        ? this.sanitizer.bypassSecurityTrustHtml(value)
        : undefined;
  }
  get iconLeft() {
    return this._iconLeft;
  }
  @Input() set iconRight(value: string | TemplateRef<any> | undefined) {
    this._iconRight = value;
    this.sanitizedIconRight =
      typeof value === 'string' && value
        ? this.sanitizer.bypassSecurityTrustHtml(value)
        : undefined;
  }
  get iconRight() {
    return this._iconRight;
  }
  @Input() selectionMode: 'single' | 'range' = 'single';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() dateFormat: string = 'dd/mm/yy';
  @Input() showClear: boolean = true;
  @Input() prefix: string = '';
  @Input() maxlength: number | string | null = null;

  @Output() onClear = new EventEmitter<void>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onIconLeftClick = new EventEmitter<Event>();
  @Output() onIconRightClick = new EventEmitter<Event>();
  @Output() onFocus = new EventEmitter<Event>();
  @Output() onBlur = new EventEmitter<Event>();

  private _value: string | Date | Date[] | null = '';
  focused = false;
  sanitizedIconLeft?: SafeHtml;
  sanitizedIconRight?: SafeHtml;
  private _iconLeft?: string | TemplateRef<any>;
  private _iconRight?: string | TemplateRef<any>;

  @ViewChild('inputElement', { static: false }) inputElement?: ElementRef<HTMLInputElement>;

  constructor(private sanitizer: DomSanitizer) {}

  get iconLeftTemplate(): TemplateRef<any> | null {
    return this.iconLeft instanceof TemplateRef ? this.iconLeft : null;
  }

  get iconRightTemplate(): TemplateRef<any> | null {
    return this.iconRight instanceof TemplateRef ? this.iconRight : null;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: string | Date | Date[] | null | undefined): void {
    this.value = value === null || value === undefined ? '' : value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    if (this.readonly) return;
    const value = (event.target as HTMLInputElement).value;
    this.value = value;
    this.onChange(value);
  }

  onCurrencyChange(value: any): void {
    this.onChange(value);
  }

  onInputBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  onInputFocus(): void {
    this.focused = true;
  }

  onFocusHandler(event: Event): void {
    if (this.disabled || this.readonly) return;

    this.onInputFocus();
    this.onFocus.emit(event);
  }

  onBlurHandler(event: Event): void {
    if (this.disabled || this.readonly) return;

    // avoid blocking another event
    setTimeout(() => {
      this.onInputBlur();
      this.onBlur.emit(event);
    });
  }

  onCalendarSelect(event: any) {
    this.value = event;
    this.onChange(event);
    this.onTouched();
    this.onSelect.emit(event);
  }

  onCalendarClear() {
    this.value = null;
    this.onChange(null);
    this.onClear.emit();
  }

  get calendarMinDate(): Date {
    return this.minDate === null ? new Date(1900, 0, 1) : this.minDate;
  }

  get calendarMaxDate(): Date {
    return this.maxDate === null ? new Date(2100, 0, 1) : this.maxDate;
  }

  /**
   * Handles click events on the left icon
   * @param event - The click event
   */
  onLeftIconClick(event: Event): void {
    if (this.disabled || this.readonly) return;
    this.onIconLeftClick.emit(event);
  }

  /**
   * Handles click events on the right icon
   * @param event - The click event
   */
  onRightIconClick(event: Event): void {
    if (this.disabled || this.readonly) return;
    this.onIconRightClick.emit(event);
  }
}
